/**
 * Design system — single source of truth for layout + typography utility classes.
 *
 * Tailwind primitives (text-hero, text-body, …) stay in tailwind.config.js.
 * Semantic compositions live here and are imported across pages/components.
 *
 * Do not duplicate these strings in individual pages; extend this file instead.
 */

// ---------------------------------------------------------------------------
// Layout — shells & vertical rhythm
// ---------------------------------------------------------------------------

/**
 * Primary shell (1440px) — Work, About, /for/…, and the site chrome.
 * Header and Footer use this too, so their contents line up with the page
 * body on viewports wider than 1440px instead of running past it.
 */
export const primaryPageShellClass =
  "w-full max-w-page mx-auto px-6 lg:px-12";

/** Case-study / blocks column (1280px shell) */
export const projectPageShellClass =
  "max-w-[1280px] mx-auto w-full px-6 lg:px-12";

/** Long-page bottom padding */
export const projectPageBottomClass = "pb-24 lg:pb-40";

/** Block sections — spacing between sections without rule lines */
export const blockSectionSpacingClass = "pt-16 lg:pt-20 pb-6";

// ---------------------------------------------------------------------------
// Figure aspect ratios — the four canonical shapes
// Every BlockFigure on a case-study page picks one of these so cells line up.
// ---------------------------------------------------------------------------

export const FIGURE_RATIOS = {
  /** 3 : 1 — wide banner, full-width hero strip */
  banner: "aspect-[3/1]",
  /** 16 : 9 — video / cinematic */
  video: "aspect-video",
  /** 4 : 3 — classic media; the default for paired cells */
  classic: "aspect-[4/3]",
  /** 1 : 1 — square; for triptychs and dense grids */
  square: "aspect-square",
};

/** Resolve a ratio key (or raw class) to a Tailwind aspect class. */
export function figureRatioClass(ratio = "classic") {
  return FIGURE_RATIOS[ratio] || ratio;
}

// ---------------------------------------------------------------------------
// Typography — blocks / case-study templates
// ---------------------------------------------------------------------------

export const blockHeroTitleClass =
  "text-hero font-light tracking-tight text-black dark:text-white";

/** Compact project hero title — between text-hero and text-section. */
export const blockProjectTitleClass =
  "text-projectTitle font-light tracking-tight text-black dark:text-white";

export const blockSectionTitleClass =
  "text-section font-light tracking-tight text-black dark:text-white";

/**
 * Small label / kicker — sans, sentence case.
 * For mono + caps (catalogue rows), use blockTechnicalLabelClass.
 */
export const blockMonoLabelClass =
  "text-meta font-sans font-normal normal-case tracking-normal text-content-muted dark:text-neutral-500";

export const blockTechnicalLabelClass =
  "font-mono uppercase text-meta text-content-muted dark:text-neutral-500";

export const blockLedeClass =
  "text-body max-w-[56ch] font-light text-content-secondary dark:text-neutral-400 leading-relaxed";

export const blockBodyClass =
  "text-body max-w-[74ch] font-light text-black dark:text-neutral-100";

export const blockMetaLineClass =
  "text-body font-sans font-normal normal-case text-content-muted dark:text-neutral-500 leading-relaxed max-w-[74ch]";

/** Second line under hero / lede (credits, tags) */
export const blockHeroStackedMetaClass =
  "pt-1 text-content-muted dark:text-neutral-500";

/** Outer spacing for BlockText sections — inner paragraphs use blockBodyClass width */
export const blockSectionBodyWrapClass = "mt-6 mb-8";

// ---------------------------------------------------------------------------
// Typography — one-off compositions used on specific templates
// ---------------------------------------------------------------------------

/** Wide secondary intro (e.g. SILT section intros) */
export const blockWideSecondaryIntroClass =
  "mt-6 mb-8 max-w-[74ch] text-body leading-relaxed text-content-secondary dark:text-neutral-400 font-light";

/** Muted auxiliary line (sequence strips, captions) */
export const blockAuxiliaryMutedClass =
  "text-body text-content-muted dark:text-neutral-500";

/** Photo / credit line */
export const blockItalicSecondaryNoteClass =
  "mt-4 mb-6 max-w-[65ch] text-body leading-relaxed italic text-content-secondary dark:text-neutral-400 font-light";

// ---------------------------------------------------------------------------
// Header — site chrome
// ---------------------------------------------------------------------------

export const headerBrandLinkClass =
  "font-sans text-body font-medium uppercase tracking-[0.18em] text-black dark:text-white hover:opacity-60 transition-opacity clickable shrink-0";

export const headerPrivateBadgeClass =
  "shrink-0 inline-flex items-center justify-center px-2 pt-px h-[24px] sm:h-6 font-mono text-meta uppercase tracking-[0.18em] leading-none bg-black text-white dark:bg-white dark:text-black";

/** Work / About (desktop) — pass active */
export function headerPrimaryNavLinkClass(isActive) {
  return [
    "font-sans text-body font-medium uppercase tracking-[0.18em]",
    "transition-opacity clickable",
    isActive
      ? "text-black dark:text-white"
      : "text-content-muted dark:text-neutral-500 hover:text-black dark:hover:text-white",
  ].join(" ");
}

/**
 * Category filter pills — pass active. Defined here with the header type
 * because it shares that scale, but the filter itself sits on the landing page.
 */
export function filterPillClass(isActive) {
  return [
    "relative py-2 clickable text-body font-light tracking-wide transition-colors duration-200",
    isActive
      ? "text-black dark:text-white"
      : "text-content-muted dark:text-neutral-500 hover:text-black dark:hover:text-white",
  ].join(" ");
}

/** Prev/next project links (desktop centre nav) */
export const headerAdjacentProjectLinkClass = [
  "group relative py-2 clickable max-w-[min(220px,28vw)] min-w-0",
  "text-body font-light tracking-wide transition-colors duration-200",
  "text-content-muted dark:text-neutral-500 hover:text-black dark:hover:text-white",
  "flex items-center gap-1.5",
].join(" ");

export const headerAdjacentProjectArrowClass = [
  "shrink-0 text-body leading-none font-normal text-content-muted dark:text-neutral-500",
  "transition-colors duration-200 group-hover:text-black dark:group-hover:text-white",
].join(" ");

export const headerAdjacentProjectUnderlineClass =
  "absolute -bottom-0.5 left-0 right-0 h-px bg-black dark:bg-white origin-left transition-transform duration-200 scale-x-0 group-hover:scale-x-100";

export const headerAdjacentProjectDotClass =
  "shrink-0 text-content-muted dark:text-neutral-600 text-xs opacity-60";

/** Mobile primary links (Work / About) */
export const headerMobilePrimaryLinkClass =
  "font-sans text-lede font-medium uppercase tracking-[0.18em] text-black dark:text-white transition-opacity clickable";

/** Mobile section label (Projects) */
export const headerMobileSectionLabelClass =
  "font-mono text-meta uppercase tracking-[0.18em] text-content-muted";

/** Mobile prev/next project rows */
export const headerMobileAdjacentProjectLinkClass =
  "text-body font-light tracking-wide text-center text-content-muted hover:text-black dark:hover:text-white transition-colors clickable max-w-full truncate px-2";

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

// `leading-6` over text-meta's own 1.35 is deliberate: it gives each footer row a
// 24px line box, so sitting on the footer's 24px bottom padding it occupies the
// same band as the scroll thumb parked at the end of the page (bottomGap 24,
// height 24 in CustomScrollbar). Both then read at the same height.
export const footerTaglineClass =
  "font-mono text-meta leading-6 uppercase text-content-muted";

export const footerLinkClass =
  "font-mono text-meta leading-6 uppercase text-content-muted hover:text-black dark:hover:text-white transition-colors clickable";

// ---------------------------------------------------------------------------
// Home — project rail
// ---------------------------------------------------------------------------

export const railEmptyStateClass =
  "text-body font-light text-center px-6 max-w-[22ch] leading-relaxed text-content-muted";

export const railMetaRowClass =
  "flex items-center justify-between text-meta font-sans tabular-nums text-content-muted dark:text-neutral-500";

export const railProjectTitleClass =
  "mt-6 lg:mt-8 text-section font-light tracking-tight leading-snug text-black dark:text-white";

export const railDescriptionClass =
  "mt-4 text-body font-light text-content-secondary dark:text-neutral-400 max-w-[34ch] leading-relaxed";

export const railDetailsListClass =
  "mt-8 grid grid-cols-2 gap-x-6 gap-y-5 max-w-[34ch] text-body font-light";

export const railDetailLabelClass =
  "text-content-muted dark:text-neutral-500";

export const railDetailValueClass =
  "mt-1 text-black dark:text-white";

export const railOpenButtonClass = [
  "mt-8 self-start clickable cursor-none inline-flex items-center justify-between gap-4",
  "min-w-[8.5rem] px-4 py-3 border border-black dark:border-white",
  "font-sans text-body font-normal uppercase tracking-[0.14em] text-black dark:text-white",
  "transition-colors duration-150 hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black",
].join(" ");

// ---------------------------------------------------------------------------
// Home — landing grid tile index badge
// ---------------------------------------------------------------------------

export const landingTileIndexBadgeClass =
  "absolute top-0 left-0 z-[1] w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center bg-black text-white dark:bg-white dark:text-black font-mono text-meta uppercase tracking-wider transition-opacity duration-150";

// ---------------------------------------------------------------------------
// Actions — the two button shapes used across About, /for/… and the rail.
// Canonical versions of strings that are currently inlined per page; new pages
// should import these instead of repeating the class list.
// ---------------------------------------------------------------------------

/** Filled — one per view, for the single most useful action. */
export const actionButtonPrimaryClass = [
  "clickable cursor-none inline-flex items-center justify-between gap-3",
  "px-4 py-[10px] border border-black dark:border-white",
  "bg-black text-white dark:bg-white dark:text-black",
  "font-mono text-meta tracking-[0.18em] uppercase",
  "hover:opacity-85 transition-opacity duration-150",
].join(" ");

/** Outline, inverts on hover — for everything else. */
export const actionButtonSecondaryClass = [
  "clickable cursor-none inline-flex items-center justify-between gap-3",
  "px-4 py-[10px] border border-black dark:border-white",
  "font-mono text-meta tracking-[0.18em] uppercase text-black dark:text-white",
  "transition-colors duration-150",
  "hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black",
].join(" ");

// ---------------------------------------------------------------------------
// Home — the landing page (route /)
// Names only, shaped like the About timeline: a mono year in a narrow left
// column, then the project name in ink with its descriptor trailing in grey.
// No rules — the page's only hairline is the one above the filter — and no
// third type size. Below lg each entry carries a thumbnail, since there is no
// hover there to answer.
// ---------------------------------------------------------------------------

/**
 * Masthead line. Wide enough to hold the disciplines on one line at xl.
 *
 * `-ml-px` corrects the first glyph's left side bearing. Bearing scales with
 * type size, so at 30px the cap starts about a pixel further in than it does on
 * the 15px type in the header and the entries below; without the pull, the
 * largest line on the page is the one that looks indented.
 */
export const homeIntroTitleClass =
  "-ml-px max-w-[60ch] text-projectTitle font-normal tracking-tight text-black dark:text-white";

/** Separators inside the masthead, or a muted continuation if copy returns. */
export const homeIntroTitleMutedClass =
  "font-light text-content-muted dark:text-neutral-500";

/**
 * The line introducing the work, set in About's paragraph exactly. No top
 * margin: it shares a row with the action buttons and starts level with them.
 */
export const homeIntroStatementClass =
  "max-w-[60ch] text-body font-light leading-relaxed text-content-secondary dark:text-neutral-400";

/** "Selected work" strip above the index. */
export const homeSectionLabelClass =
  "font-mono text-meta uppercase tracking-[0.18em] text-content-muted dark:text-neutral-500";

/** Tally that trails a filter pill. */
export const homeFilterCountClass =
  "ml-1.5 font-mono text-meta tabular-nums text-content-subtle dark:text-neutral-600";

export const homeEmptyStateClass =
  "py-16 text-body font-light text-content-muted dark:text-neutral-500";

/** Thumbnail carried by the entry itself — touch screens have no hover. */
export const homeEntryThumbClass = [
  "lg:hidden relative w-14 h-14 overflow-hidden",
  "bg-[#F5F5F5] dark:bg-[#141414]",
  "outline outline-1 outline-border dark:outline-neutral-800 outline-offset-[-1px]",
].join(" ");

/** Preview surface. Sits inside a `relative aspect-square` holder. */
export const homePreviewFrameClass = [
  "absolute inset-0 overflow-hidden",
  "bg-[#F5F5F5] dark:bg-[#141414]",
  "outline outline-1 outline-border dark:outline-neutral-800 outline-offset-[-1px]",
].join(" ");

export const homePreviewCaptionClass =
  "mt-3 font-mono text-meta uppercase tracking-[0.14em] text-content-muted dark:text-neutral-500";

/** One entry. Entry root must carry `group`. */
export const homeTitleEntryClass = [
  "group flex w-full gap-4",
  "focus:outline-none focus-visible:outline focus-visible:outline-1",
  "focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-white",
].join(" ");

/** Year. About's `dt`: mono, muted, nudged onto the name's baseline. */
export const homeTitleYearClass =
  "shrink-0 whitespace-nowrap font-mono text-meta uppercase tracking-[0.12em] tabular-nums text-content-muted dark:text-neutral-500 sm:pt-[4px]";

/**
 * Project name. Dimmed while a different entry holds the preview, so the grid
 * points at the artwork instead of competing with it.
 */
export function homeTitleNameClass(isDimmed) {
  return [
    "text-body leading-snug transition-colors duration-200",
    isDimmed
      ? "text-content-muted dark:text-neutral-600"
      : "text-black dark:text-white",
  ].join(" ");
}

/** Trailing descriptor on the name line, as About sets its details. */
export const homeTitleDescriptorClass =
  "font-light text-content-secondary dark:text-neutral-400";

// ---------------------------------------------------------------------------
// Legacy project pages — prefer migrating markup to block tokens over time
// ---------------------------------------------------------------------------

/** Uses App.css `.text-section-name` + spacing */
export const legacyPageSectionHeadingClass = "text-section-name mb-6";

// ---------------------------------------------------------------------------
// Dev — project blocks preview page
// ---------------------------------------------------------------------------

export const previewSectionSubtitleClass =
  "mt-3 max-w-[70ch] text-body leading-relaxed font-light text-content-secondary dark:text-neutral-400";

export const previewClosingNoteClass =
  "mt-8 max-w-[60ch] text-body italic leading-relaxed text-content-secondary dark:text-neutral-400";
